import { useEffect, useMemo } from 'react';
import type { PropsWithChildren } from 'react';
import { getObservabilityClient } from '@locus-taxy/encore-observability/observability';
import { useIdentity } from '../auth';
import { getBugsnagClient } from '../utils/bugsnag';
import { UserContext, type UserContextValue } from './UserContextDefinition';

export function UserProvider({ children }: Readonly<PropsWithChildren>) {
  const { user, isLoading } = useIdentity();

  const contextValue = useMemo<UserContextValue>(
    () => ({ user, isLoading }),
    [user, isLoading]
  );

  useEffect(() => {
    if (!user?.userId) {
      return;
    }

    const observabilityClient = getObservabilityClient();
    const userId = user.userId;

    let uniqueUserIdentifier: string;

    if (userId === user.email) {
      // If userId is email, then split it and use the first part as the userId
      // This is to avoid passing raw email to o11y vendor as it may contain PII
      uniqueUserIdentifier = `${userId.split('@')[0]}/${user.clientId}`;
    } else {
      uniqueUserIdentifier = `${userId}/${user.clientId}`;
    }

    if (observabilityClient) {
      observabilityClient.identifyUser({
        // Avoid passing raw email to o11y vendor as it may contain PII
        // Groundcover dashboard picks email key as user identifier in UI table
        email: uniqueUserIdentifier,
        id: uniqueUserIdentifier,
      });
    }

    const bugsnagClient = getBugsnagClient();
    if (bugsnagClient) {
      bugsnagClient.setUser(
        uniqueUserIdentifier,
        undefined,
        user.name ?? undefined
      );
    }
  }, [user]);

  return (
    <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>
  );
}
